import { FunctionComponent, useRef, useState } from 'react';
import { css } from '@emotion/css';
import colors from '@/theme/colors';
import { useApolloClient } from '@apollo/client';
import { ApolloClientName } from '@/config/apolloClient';

import {
  changeEmailMessages,
  bannersMessages,
} from '@/pages/AccountInformation/AccountInformation.constants';
import { MessageBanner, PeachjarLoader } from '@/components';
import { ButtonFlatSmall, ButtonPrimarySmall, TextInput, useSnackbar } from '@/design-system';
import emailSendIcon from '@/assets/email-send-icon.svg';
import { VERIFY_CHANGE_EMAIL } from '@/graphql/mutation/VerifyChangeEmail.graphql';
import { VerifyChangeEmailResponse } from '@/graphql/types';
import SpinnerOverlay from '@/components/SpinnerOverlay/SpinnerOverlay';
import emailIsValidish from '@/utils/formUtils';
import { useLiveFeedback } from '@/hooks';

type Props = {
  email: string;
  disabled?: boolean;
  updateRevertBlock: () => void;
};

const ChangeEmailForm: FunctionComponent<Props> = ({
  email,
  disabled = false,
  updateRevertBlock,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState(email);
  const [inputEmail, setInputEmail] = useState(email);
  const [showBannerVerifyEmail, setShowBannerVerifyEmail] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [alertMessage, setAlertMessage] = useLiveFeedback({ delay: 2000 });
  const inputRef = useRef<HTMLInputElement>(null);

  const showToast = useSnackbar();
  const client = useApolloClient();
  const isFormValid = dirty && !error;

  const handleInputChange = v => {
    const newEmailInput = v.target.value;
    setError(emailIsValidish(newEmailInput) ? null : changeEmailMessages.invalidEmailError);
    setInputEmail(newEmailInput);
    setDirty(true);
  };

  const resetFormState = (emailState: string) => {
    setError(null);
    setDirty(false);
    setDefaultEmail(emailState);
    setInputEmail(emailState);
    inputRef.current?.focus();
  };

  const handleCancel = () => {
    resetFormState(defaultEmail);
  };

  const handleSendVerificationEmail = async () => {
    setIsLoading(true);
    try {
      const { data } = await client.mutate<VerifyChangeEmailResponse>({
        context: {
          clientName: ApolloClientName.AccountsBff,
        },
        mutation: VERIFY_CHANGE_EMAIL,
        variables: {
          input: {
            userId: 1,
            email: inputEmail,
            hierarchyType: 'parent',
          },
        },
      });

      if (data) {
        const { verifyChangeEmail } = data;
        resetFormState(verifyChangeEmail.email);
        setShowBannerVerifyEmail(true);
        updateRevertBlock();
        setAlertMessage('Verification email sent successfully.');
      }
    } catch (error) {
      showToast({ message: changeEmailMessages.updatedError });
      setAlertMessage('Failed to send verification email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PeachjarLoader loading={isLoading} />
      <div aria-live="assertive" role="alert" className="sr-only">
        {alertMessage}
      </div>
      <TextInput
        ref={inputRef}
        label="Primary Email Address"
        value={inputEmail}
        error={!!error}
        type="email"
        autoComplete="email"
        helperText={error}
        onChange={handleInputChange}
        disabled={disabled}
        data-testid="emailInput"
        aria-label="Primary Email Address"
        inputProps={{
          'aria-describedby': showBannerVerifyEmail
            ? 'change-email-verification-message'
            : undefined,
        }}
        autoFocus
      />
      {showBannerVerifyEmail && (
        <div className={styles.content}>
          <MessageBanner
            id="change-email-verification-message"
            iconSrc={emailSendIcon}
            subheadline={bannersMessages.emailNeedsToBeVerified}
            variant="warning"
          />
        </div>
      )}
      {dirty && (
        <div className={styles.buttonBar}>
          <div className={styles.secondaryCta}>
            <ButtonFlatSmall
              onClick={handleCancel}
              disabled={!dirty}
              data-testid="cancelEmailButton"
            >
              Cancel
            </ButtonFlatSmall>
          </div>
          <div>
            <ButtonPrimarySmall
              // loading={isLoading}
              onClick={handleSendVerificationEmail}
              disabled={!isFormValid}
              data-testid="updateEmailButton"
            >
              Update
            </ButtonPrimarySmall>
          </div>
        </div>
      )}
    </>
  );
};

export default ChangeEmailForm;

const styles = {
  content: css`
    margin-bottom: 18px;
    margin-top: 32px;
    & .MuiAlert-standardWarning {
      border: none !important;
    }
  `,
  emailDisclaimer: css`
    color: ${colors.slate};
    font-size: 13px;
  `,
  subscriptionDisclaimer: css`
    color: ${colors.prussian};
    font-size: 16px;
  `,
  secondaryCta: css`
    margin-right: 16px;
  `,
  buttonBar: css`
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;
  `,
  link: css`
    color: ${colors.jungle} !important;
    cursor: pointer;
    &:hover {
      text-decotarion: underline;
    }
  `,
};
